import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { SortableTh } from '../components/SortableTh'
import { useTableSort } from '../hooks/useTableSort'
import {
  MODALIDADE_GRUPOS_ATIVOS,
  type ModalidadeGrupoId,
} from '../lib/modalidadeGrupos'
import { fetchConsumoPrevisto } from '../lib/supabaseQuery'
import { resolvePrecoVigentePorProduto } from '../lib/precoVigente'
import { supabase } from '../lib/supabase'
import { formatCurrency, formatNumber, todayISO } from '../lib/utils'
import type { ConsumoPrevisto, ModalidadeEnsino, Preco, Rota } from '../types/database'
import { GRUPOS } from '../types/database'

type ColunaAlimentoId = ModalidadeGrupoId | 'medio'

type ColunaAlimento = {
  id: ColunaAlimentoId
  label: string
  keys: readonly ModalidadeEnsino[]
}

/** Colunas alinhadas ao cadastro novo + Médio (fora dos grupos UI). */
const COLUNAS_ALIMENTOS: readonly ColunaAlimento[] = [
  ...MODALIDADE_GRUPOS_ATIVOS.map((g) => ({
    id: g.id as ColunaAlimentoId,
    label: g.label,
    keys: g.keys,
  })),
  { id: 'medio', label: 'Médio', keys: ['medio'] as const },
]

const MODALIDADE_PARA_COLUNA = new Map<ModalidadeEnsino, ColunaAlimentoId>(
  COLUNAS_ALIMENTOS.flatMap((c) => c.keys.map((k) => [k, c.id] as const)),
)

type ModalidadesColuna = Record<ColunaAlimentoId, number>

type AlimentoEnriched = {
  escola_id: string
  escola: string
  rota_id: string | null
  alimento_id: string
  alimento: string
  grupo: string
  unidade: string
  modalidades: ModalidadesColuna
  qtd_total: number
  custo: number
}

function emptyModalidades(): ModalidadesColuna {
  return Object.fromEntries(COLUNAS_ALIMENTOS.map((c) => [c.id, 0])) as ModalidadesColuna
}

/** Converte gramas da view para kg (unidade de compra/estoque). */
function gramsToKg(qtdG: number): number {
  return qtdG / 1000
}

function aggregateConsumo(rows: ConsumoPrevisto[]): AlimentoEnriched[] {
  const map = new Map<string, AlimentoEnriched>()
  for (const r of rows) {
    const key = `${r.escola_id}:${r.alimento_id}`
    let item = map.get(key)
    if (!item) {
      item = {
        escola_id: r.escola_id,
        escola: r.escola,
        rota_id: r.rota_id,
        alimento_id: r.alimento_id,
        alimento: r.alimento,
        grupo: r.grupo,
        unidade: 'kg',
        modalidades: emptyModalidades(),
        qtd_total: 0,
        custo: 0,
      }
      map.set(key, item)
    }
    const qtd = gramsToKg(Number(r.quantidade) || 0)
    const col = MODALIDADE_PARA_COLUNA.get(r.modalidade)
    if (col) item.modalidades[col] += qtd
    item.qtd_total += qtd
  }
  return [...map.values()]
}

type EscolaOption = { id: string; nome: string; rota_id: string | null }

type GrupoEscola = {
  escola_id: string
  escola: string
  rota_id: string | null
  custo_total: number
  modalidades: ModalidadesColuna
  qtd_total: number
  unidade_resumo: string | null
  produtos: AlimentoEnriched[]
}

function groupByEscola(items: AlimentoEnriched[]): GrupoEscola[] {
  const map = new Map<string, AlimentoEnriched[]>()
  for (const r of items) {
    const list = map.get(r.escola_id) || []
    list.push(r)
    map.set(r.escola_id, list)
  }

  return [...map.values()].map((produtos) => {
    const sorted = [...produtos].sort((a, b) => a.alimento.localeCompare(b.alimento, 'pt-BR'))
    const unidades = new Set(sorted.map((p) => p.unidade))
    const sameUnit = unidades.size === 1
    const unidade_resumo = sameUnit ? sorted[0].unidade : null

    const modalidades = COLUNAS_ALIMENTOS.reduce((acc, c) => {
      acc[c.id] = sameUnit ? sorted.reduce((s, p) => s + p.modalidades[c.id], 0) : 0
      return acc
    }, emptyModalidades())

    const qtd_total = sameUnit ? sorted.reduce((s, p) => s + p.qtd_total, 0) : 0
    const custo_total = sorted.reduce((s, p) => s + p.custo, 0)

    return {
      escola_id: sorted[0].escola_id,
      escola: sorted[0].escola,
      rota_id: sorted[0].rota_id,
      custo_total,
      modalidades,
      qtd_total,
      unidade_resumo,
      produtos: sorted,
    }
  })
}

function ModalidadeCells({
  modalidades,
  unidade,
  unidadeResumo,
}: {
  modalidades: ModalidadesColuna
  unidade: string
  unidadeResumo?: string | null
}) {
  const showQty = unidadeResumo !== null
  const unit = unidadeResumo ?? unidade
  return (
    <>
      {COLUNAS_ALIMENTOS.map((c) => (
        <td key={c.id} className="col-num">
          {showQty ? `${formatNumber(modalidades[c.id], 3)} ${unit}` : '—'}
        </td>
      ))}
    </>
  )
}

export function AlimentosPage() {
  const { canViewValores } = useAuth()
  const [rows, setRows] = useState<ConsumoPrevisto[]>([])
  const [precos, setPrecos] = useState<Preco[]>([])
  const [rotas, setRotas] = useState<Rota[]>([])
  const [escolas, setEscolas] = useState<EscolaOption[]>([])
  const [alimentos, setAlimentos] = useState<
    { id: string; nome: string; categoria: string; produto_id: string | null }[]
  >([])
  const [produtoId, setProdutoId] = useState('')
  const [diasLetivos, setDiasLetivos] = useState(20)
  const [diasLetivosManual, setDiasLetivosManual] = useState(false)
  const [filtroRota, setFiltroRota] = useState('')
  const [filtroEscola, setFiltroEscola] = useState('')
  const [filtroGrupo, setFiltroGrupo] = useState('')
  const [inicio, setInicio] = useState('')
  const [fim, setFim] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadingCalculo, setLoadingCalculo] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedEscolas, setExpandedEscolas] = useState<Set<string>>(new Set())
  const tableSort = useTableSort<AlimentoEnriched>('escola', 'asc')
  const escolaSort = useTableSort<GrupoEscola>('escola', 'asc')
  const calcSeqRef = useRef(0)

  const modoAgrupado = !produtoId

  const loadMeta = useCallback(async () => {
    setLoading(true)
    const [prec, rot, alim, esc] = await Promise.all([
      supabase.from('precos').select('*').order('criado_em', { ascending: true }),
      supabase.from('rotas').select('*').order('nome'),
      supabase.from('alimentos_macronutrientes').select('id, nome, categoria, produto_id').order('nome'),
      supabase.from('escolas').select('id, nome, rota_id').order('nome'),
    ])
    if (prec.error || rot.error || alim.error || esc.error) {
      setError(prec.error?.message || rot.error?.message || alim.error?.message || esc.error?.message || 'Erro')
    }
    setPrecos((prec.data as Preco[]) || [])
    setRotas((rot.data as Rota[]) || [])
    setAlimentos((alim.data as typeof alimentos) || [])
    setEscolas((esc.data as EscolaOption[]) || [])
    setLoading(false)
  }, [])

  const loadCalculo = useCallback(async () => {
    if (!inicio || !fim) {
      calcSeqRef.current++
      setRows([])
      return
    }
    setLoadingCalculo(true)
    const seq = ++calcSeqRef.current
    const { data, error: calcError } = await fetchConsumoPrevisto({
      inicio,
      fim,
      alimentoId: produtoId || undefined,
      rotaId: filtroRota || undefined,
      grupo: filtroGrupo || undefined,
      escolaId: filtroEscola || undefined,
    })
    if (seq !== calcSeqRef.current) return
    if (calcError) setError(calcError)
    else setError(null)
    setRows(data)
    setLoadingCalculo(false)
  }, [inicio, fim, produtoId, filtroRota, filtroGrupo, filtroEscola])

  useEffect(() => {
    document.title = 'Alimentos · ISEN EDUCAE'
    loadMeta()
  }, [loadMeta])

  useEffect(() => {
    if (loading) return
    loadCalculo()
  }, [loading, loadCalculo])

  useEffect(() => {
    if (!inicio || !fim || inicio > fim || diasLetivosManual) return
    let cancelled = false
    void supabase.rpc('dias_letivos', { inicio, fim }).then(({ data, error: err }) => {
      if (cancelled) return
      if (err) setError(err.message)
      else setDiasLetivos(Number(data) || 0)
    })
    return () => {
      cancelled = true
    }
  }, [inicio, fim, diasLetivosManual])

  const alimentosFiltrados = useMemo(() => {
    if (!filtroGrupo) return alimentos
    return alimentos.filter((a) => a.categoria === filtroGrupo)
  }, [alimentos, filtroGrupo])

  const escolasFiltradas = useMemo(() => {
    if (!filtroRota) return escolas
    return escolas.filter((e) => e.rota_id === filtroRota)
  }, [escolas, filtroRota])

  function onGrupoChange(value: string) {
    setFiltroGrupo(value)
    if (!produtoId) return
    const atual = alimentos.find((a) => a.id === produtoId)
    if (value && atual && atual.categoria !== value) setProdutoId('')
  }

  function onRotaChange(value: string) {
    setFiltroRota(value)
    if (!filtroEscola) return
    const atual = escolas.find((e) => e.id === filtroEscola)
    if (value && atual && atual.rota_id !== value) setFiltroEscola('')
  }

  function onInicioChange(value: string) {
    setInicio(value)
    setDiasLetivosManual(false)
  }

  function onFimChange(value: string) {
    setFim(value)
    setDiasLetivosManual(false)
  }

  function toggleEscola(id: string) {
    setExpandedEscolas((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const precoMap = useMemo(() => {
    const refDate = fim || todayISO()
    const m = new Map<string, number>()
    for (const alimento of alimentos) {
      const vigente = resolvePrecoVigentePorProduto(precos, alimento.produto_id, refDate)
      if (vigente) m.set(alimento.id, Number(vigente.valor_unitario))
    }
    return m
  }, [precos, fim, alimentos])

  const enriched = useMemo((): AlimentoEnriched[] => {
    return aggregateConsumo(rows).map((r) => {
      const preco = precoMap.get(r.alimento_id) || 0
      return { ...r, custo: r.qtd_total * preco }
    })
  }, [rows, precoMap])

  const enrichedSorted = useMemo(() => {
    const accessors: Record<string, (r: AlimentoEnriched) => string | number> = {
      escola: (r) => r.escola,
      rota: (r) => rotas.find((x) => x.id === r.rota_id)?.nome || '',
      qtd_total: (r) => r.qtd_total,
      custo: (r) => r.custo,
    }
    for (const c of COLUNAS_ALIMENTOS) {
      accessors[c.id] = (r) => r.modalidades[c.id]
    }
    return tableSort.sortRows(enriched, accessors)
  }, [enriched, rotas, tableSort])

  const gruposEscola = useMemo(() => groupByEscola(enriched), [enriched])

  const gruposEscolaSorted = useMemo(() => {
    const accessors: Record<string, (g: GrupoEscola) => string | number> = {
      escola: (g) => g.escola,
      rota: (g) => rotas.find((x) => x.id === g.rota_id)?.nome || '',
      qtd_total: (g) => g.qtd_total,
      custo: (g) => g.custo_total,
    }
    for (const c of COLUNAS_ALIMENTOS) {
      accessors[c.id] = (g) => g.modalidades[c.id]
    }
    return escolaSort.sortRows(gruposEscola, accessors)
  }, [gruposEscola, rotas, escolaSort])

  const totaisPorRota = useMemo(() => {
    const m = new Map<string, { qtd: number; custo: number }>()
    for (const r of enriched) {
      const key = r.rota_id || 'sem'
      const cur = m.get(key) || { qtd: 0, custo: 0 }
      cur.qtd += r.qtd_total
      cur.custo += r.custo
      m.set(key, cur)
    }
    return m
  }, [enriched])

  const totalGeral = useMemo(
    () =>
      enriched.reduce(
        (s, r) => ({ qtd: s.qtd + r.qtd_total, custo: s.custo + r.custo }),
        { qtd: 0, custo: 0 },
      ),
    [enriched],
  )

  const colsBeforeTotals = 2 + COLUNAS_ALIMENTOS.length
  const colsAfterTotals = canViewValores ? 2 : 1
  const sortState = modoAgrupado ? escolaSort : tableSort
  const hasRows = modoAgrupado ? gruposEscolaSorted.length > 0 : enrichedSorted.length > 0

  function expandAll() {
    setExpandedEscolas(new Set(gruposEscolaSorted.map((g) => g.escola_id)))
  }

  function collapseAll() {
    setExpandedEscolas(new Set())
  }

  if (loading) return <div className="loading">Carregando cálculo…</div>

  return (
    <div>
      <div className="page-header">
        <h1>Cálculo de alimentos</h1>
        {loadingCalculo && (
          <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>Atualizando…</span>
        )}
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      {(!inicio || !fim) && (
        <div className="alert alert-info">Informe o período (início e fim) para calcular o consumo previsto.</div>
      )}
      <div className="filters">
        <div className="filters-group">
          <div className="field">
            <label>Rota</label>
            <select value={filtroRota} onChange={(e) => onRotaChange(e.target.value)}>
              <option value="">Todas</option>
              {rotas.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nome}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ minWidth: 220 }}>
            <label>Escola</label>
            <select value={filtroEscola} onChange={(e) => setFiltroEscola(e.target.value)}>
              <option value="">Todas</option>
              {escolasFiltradas.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nome}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="filters-group">
          <div className="field">
            <label>Grupo</label>
            <select value={filtroGrupo} onChange={(e) => onGrupoChange(e.target.value)}>
              <option value="">Todos</option>
              {GRUPOS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ minWidth: 220 }}>
            <label>Produto *</label>
            <select value={produtoId} onChange={(e) => setProdutoId(e.target.value)}>
              <option value="">Todos os produtos</option>
              {alimentosFiltrados.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nome}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="filters-group">
          <div className="field">
            <label>Início</label>
            <input type="date" value={inicio} onChange={(e) => onInicioChange(e.target.value)} />
          </div>
          <div className="field">
            <label>Fim</label>
            <input type="date" value={fim} onChange={(e) => onFimChange(e.target.value)} />
          </div>
          <div className="field">
            <label>Dias letivos *</label>
            <input
              type="number"
              min={0}
              value={diasLetivos}
              onChange={(e) => {
                setDiasLetivos(Number(e.target.value) || 0)
                setDiasLetivosManual(true)
              }}
            />
          </div>
        </div>
      </div>
      {modoAgrupado && hasRows && (
        <div className="table-toolbar">
          <button type="button" className="btn btn-ghost btn-sm" onClick={expandAll}>
            Expandir todas
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={collapseAll}>
            Recolher todas
          </button>
        </div>
      )}
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh
                label="Escola"
                sortKey="escola"
                activeKey={sortState.sortKey}
                direction={sortState.sortDir}
                onSort={sortState.toggleSort}
                col="name"
              />
              <SortableTh
                label="Rota"
                sortKey="rota"
                activeKey={sortState.sortKey}
                direction={sortState.sortDir}
                onSort={sortState.toggleSort}
                col="text"
              />
              {COLUNAS_ALIMENTOS.map((c) => (
                <SortableTh
                  key={c.id}
                  label={c.label}
                  sortKey={c.id}
                  activeKey={sortState.sortKey}
                  direction={sortState.sortDir}
                  onSort={sortState.toggleSort}
                  col="num"
                />
              ))}
              <SortableTh
                label="Total"
                sortKey="qtd_total"
                activeKey={sortState.sortKey}
                direction={sortState.sortDir}
                onSort={sortState.toggleSort}
                col="num"
              />
              {canViewValores && (
                <SortableTh
                  label="Custo estimado"
                  sortKey="custo"
                  activeKey={sortState.sortKey}
                  direction={sortState.sortDir}
                  onSort={sortState.toggleSort}
                  col="currency"
                />
              )}
            </tr>
          </thead>
          <tbody>
            {!hasRows ? (
              <tr>
                <td colSpan={colsBeforeTotals + colsAfterTotals} className="col-text">
                  Nenhum resultado para os filtros selecionados.
                </td>
              </tr>
            ) : modoAgrupado ? (
              gruposEscolaSorted.flatMap((g) => {
                const expanded = expandedEscolas.has(g.escola_id)
                const parent = (
                  <tr
                    key={g.escola_id}
                    className="row-escola"
                    onClick={() => toggleEscola(g.escola_id)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={expanded}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        toggleEscola(g.escola_id)
                      }
                    }}
                  >
                    <td className="col-name">
                      <span className="row-expand-icon" aria-hidden>
                        {expanded ? '▾' : '▸'}
                      </span>{' '}
                      {g.escola}
                    </td>
                    <td className="col-text">{rotas.find((x) => x.id === g.rota_id)?.nome || '—'}</td>
                    <ModalidadeCells
                      modalidades={g.modalidades}
                      unidade=""
                      unidadeResumo={g.unidade_resumo}
                    />
                    <td className="col-num">
                      {g.unidade_resumo
                        ? `${formatNumber(g.qtd_total, 3)} ${g.unidade_resumo}`
                        : '—'}
                    </td>
                    {canViewValores && (
                      <td className="col-currency row-escola-custo">{formatCurrency(g.custo_total)}</td>
                    )}
                  </tr>
                )
                const children = expanded
                  ? g.produtos.map((r) => (
                      <tr key={`${r.escola_id}-${r.alimento_id}`} className="row-produto">
                        <td className="col-name">
                          <span className="row-produto-label">{r.alimento}</span>
                        </td>
                        <td className="col-text">—</td>
                        <ModalidadeCells modalidades={r.modalidades} unidade={r.unidade} />
                        <td className="col-num">
                          {formatNumber(r.qtd_total, 3)} {r.unidade}
                        </td>
                        {canViewValores && (
                          <td className="col-currency">{formatCurrency(r.custo)}</td>
                        )}
                      </tr>
                    ))
                  : []
                return [parent, ...children]
              })
            ) : (
              enrichedSorted.map((r) => (
                <tr key={`${r.escola_id}-${r.alimento_id}`}>
                  <td className="col-name">{r.escola}</td>
                  <td className="col-text">{rotas.find((x) => x.id === r.rota_id)?.nome || '—'}</td>
                  <ModalidadeCells modalidades={r.modalidades} unidade={r.unidade} />
                  <td className="col-num">
                    {formatNumber(r.qtd_total, 3)} {r.unidade}
                  </td>
                  {canViewValores && (
                    <td className="col-currency">{formatCurrency(r.custo)}</td>
                  )}
                </tr>
              ))
            )}
          </tbody>
          {hasRows && (
            <tfoot>
              <tr>
                <td colSpan={colsBeforeTotals} className="col-name">
                  <strong>Totais por rota</strong>
                </td>
                <td colSpan={colsAfterTotals} className="col-text">
                  {[...totaisPorRota.entries()].map(([id, t]) => (
                    <div key={id}>
                      {rotas.find((r) => r.id === id)?.nome || 'Sem rota'}: {formatNumber(t.qtd, 2)}
                      {canViewValores && <> · {formatCurrency(t.custo)}</>}
                    </div>
                  ))}
                </td>
              </tr>
              <tr>
                <td colSpan={colsBeforeTotals} className="col-name">
                  <strong>Total geral</strong>
                </td>
                <td className="col-num">{formatNumber(totalGeral.qtd, 2)}</td>
                {canViewValores && (
                  <td className="col-currency">{formatCurrency(totalGeral.custo)}</td>
                )}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}
